import { Injectable, Injector } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map, tap } from 'rxjs';
import { CustomModal } from '../../models/CustomModal.model';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { DynamicModalComponent } from '../../components/dynamic-modal/dynamic-modal.component';
import { EditPathUserModalComponent, EDIT_PATH_USER_MODAL_KEY, EDIT_PATH_USER_MODAL_DATA } from '../../components/edit-path-user-modal/edit-path-user-modal.component';
import { environment } from '../../environment/environment';

@Injectable({
  providedIn: 'root'
})
export class ModalService {
  private apiUrl = `${environment.apiUrl}`;
  private activeModals = new Map<string, { overlayRef: OverlayRef; componentRef: any }>();
  private availableModals: CustomModal[] = [];
  private modalsLoaded = false;

  constructor(
    private http: HttpClient,
    private overlay: Overlay,
    private injector: Injector
  ) {
    this.loadAvailableModals();
  }

  private loadAvailableModals(): void {
    this.getModals().subscribe({
      next: (modals) => {
        this.availableModals = modals;
        this.modalsLoaded = true;
      },
      error: (error) => {
        console.error('[ModalService] Error loading available modals:', error);
      }
    });
  }

  getModals(): Observable<CustomModal[]> {
    return this.http.get<any[]>(`${this.apiUrl}/modals`).pipe(
      map(modals => modals.map(modal => CustomModal.fromDTO(modal))),
    );
  }

  getModal(uuid: string): Observable<CustomModal> {
    return this.http.get<any>(`${this.apiUrl}/modals/${uuid}`).pipe(
      map(modal => CustomModal.fromDTO(modal))
    );
  }

  postModal(modal: CustomModal): Observable<CustomModal> {
    const dto = CustomModal.convertToDTO(modal);
    return this.http.post<any>(`${this.apiUrl}/modals`, {modal: dto}).pipe(
      map(response => CustomModal.fromDTO(response))
    );
  }

  showModal(modalUUID: string, mode: 1 | 2 | 3 = 1, data?: any): void {
    if (this.activeModals.has(modalUUID)) {
      return;
    }

    this.getModal(modalUUID).subscribe({
      next: (modal) => {
        const overlayRef = this.createOverlay();
        const componentRef = this.createComponent(overlayRef, modal, mode, data);
        
        this.activeModals.set(modalUUID, { overlayRef, componentRef });

        overlayRef.backdropClick().subscribe(() => {
          this.closeModal(modalUUID);
        });

        overlayRef.keydownEvents().subscribe(event => {
          if (event.key === 'Escape') {
            this.closeModal(modalUUID);
          }
        });
      },
      error: (error) => {
        console.error('[ModalService] Error fetching modal:', error);
      }
    });
  }
  
  private createComponent(overlayRef: OverlayRef, modal: CustomModal, mode: 1 | 2 | 3, data?: any): any {
    const portal = new ComponentPortal(DynamicModalComponent);
    const componentRef = overlayRef.attach(portal);
    componentRef.instance.modal = modal;
    componentRef.instance.currentMode = mode;
    componentRef.instance.data = data;
    return componentRef;
  }

  closeModal(modalUUID: string): void {
    const modal = this.activeModals.get(modalUUID);
    if (modal) {
      modal.overlayRef.dispose();
      this.activeModals.delete(modalUUID);
    }
  }

  closeAllModals(): void {
    this.activeModals.forEach((modal, modalUUID) => {
      this.closeModal(modalUUID);
    });
  }

  hasActiveModals(): boolean {
    return this.activeModals.size > 0;
  }

  private createOverlay(): OverlayRef {
    const overlayRef = this.overlay.create({
      hasBackdrop: true,
      positionStrategy: this.overlay.position()
        .global()
        .centerHorizontally()
        .centerVertically(),
      scrollStrategy: this.overlay.scrollStrategies.noop(),
      width: 'auto',
      height: 'auto',
      maxWidth: '95vw',
      maxHeight: '95vh'
    });

    return overlayRef;
  }

  showModalByName(modalName: string | number | undefined | null, mode: 1 | 2 | 3 = 1, data?: any): void {
    
    if (!this.modalsLoaded) {
      setTimeout(() => this.showModalByName(modalName, mode, data), 100);
      return;
    }

    const modalNameString = String(modalName || '');
    const normalizedSearchName = modalNameString.trim().toLowerCase();
    const modal = this.availableModals.find(m => 
      m.name.trim().toLowerCase() === normalizedSearchName
    );

    if (modal) {
      this.showModal(modal.uuid, mode, data);
    } else {
      console.error(`[ModalService] Modal '${modalNameString}' not found in available modals:`, 
        this.availableModals.map(m => m.name));
    }
  }

  getModalData(modalUUID: string): any {
    return this.activeModals.get(modalUUID)?.componentRef?.instance?.data;
  }

  /**
   * Opens the dedicated Edit Path User modal with prefilled data.
   * Does not rely on API modal config or dynamic form.
   */
  showEditPathUserModal(data: { pathGuid: string; userUuid: string; full_name?: string; requirement_started_at?: string; maintenance_started_at?: string; version_id?: number | null }): void {
    const key = EDIT_PATH_USER_MODAL_KEY;
    if (this.activeModals.has(key)) {
      return;
    }
    const overlayRef = this.createOverlay();
    const customInjector = Injector.create({
      providers: [{ provide: EDIT_PATH_USER_MODAL_DATA, useValue: data }],
      parent: this.injector
    });
    const portal = new ComponentPortal(EditPathUserModalComponent, null, customInjector);
    const componentRef = overlayRef.attach(portal);
    this.activeModals.set(key, { overlayRef, componentRef });

    overlayRef.backdropClick().subscribe(() => this.closeModal(key));
    overlayRef.keydownEvents().subscribe(event => {
      if (event.key === 'Escape') this.closeModal(key);
    });
  }
}
